import Vue from 'vue';
import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnUnreadView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import AnalyticsHelper from '../../../helper/AnalyticsHelper';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import messageTranslateActions from './actions/messageTranslateActions';
import messageForwardActions from './actions/messageForwardActions';
import messageHideActions from './actions/messageHideActions';
import { debounce } from '@chatwoot/utils';
import getters, { getSelectedChatConversation } from './getters';
// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  getScheduledMessage: debounce(async ({ commit }, conversationId) => {
    if (!conversationId) {
      console.log('[VUEX ACTION] getScheduledMessage - conversationId undefined, ignorando');
      return;
    }

    try {
      commit(`scheduledMessages/${types.SET_SCHEDULED_MESSAGE}`, []);

      const response = await ConversationApi.getScheduledMessage({ conversationId });
      if (response.data) {
        commit(`scheduledMessages/${types.SET_SCHEDULED_MESSAGE}`, [response.data]);
      }
    } catch (error) {
      // Ignore error
    }
  }, 500),

  fetchAllConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    if(params.boardKey){
      commit("SET_LIST_LOADING_BY_GROUPED_BOARD", { groupedKey: params.boardKey });
    }
    try {
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.filterKey || params.assigneeType
      );
      if(params.boardKey){
        commit("CLEAR_LIST_LOADING_BY_GROUPED_BOARD", { groupedKey: params.boardKey });
      }
      return data;
    } catch (error) {
      commit(types.CLEAR_LIST_LOADING_STATUS);
      return null;
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    if(params.boardKey){
      commit("SET_LIST_LOADING_BY_GROUPED_BOARD", { groupedKey: params.boardKey });
    }
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.filterKey || 'appliedFilters'
      );
      if(params.boardKey){
        commit("CLEAR_LIST_LOADING_BY_GROUPED_BOARD", { groupedKey: params.boardKey });
      }
      return data;
    } catch (error) {
      commit(types.CLEAR_LIST_LOADING_STATUS);
      return null;
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  setConversationListNextRefreshAt({ commit }, nextRefreshAt) {
    commit(types.SET_CONV_LIST_NEXT_REFRESH_AT, nextRefreshAt);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, { id: data.conversationId });
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: data.payload,
      });
    } catch (error) {
      // Handle error
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch, state }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, { id: data.id });
    if (data.dataFetched === undefined || data.syncMessages === true) {
      try {
        let messageId;
        if (data.dataFetched !== undefined) {
          Vue.set(data, 'dataFetched', false);
          const response = await ConversationApi.show(data.id);
          if (state.selectedChatId !== data.id) {
            return;
          }
          messageId = response.data.messages[0].id;
          Vue.set(data, 'messages', response.data.messages);
        }else{
          messageId = data.messages[0].id;
        }
        await dispatch('fetchPreviousMessages', {
          after,
          before: messageId,
          conversationId: data.id,
        });
        if (state.selectedChatId !== data.id) {
          return;
        }
        if (data.dataFetched != undefined) {
          Vue.set(data, 'syncMessages', false);
        }
        Vue.set(data, 'dataFetched', true);
      } catch (error) {
        // Ignore error
      }
    }
  },

  activeSyncConversationMessages: (
    { state }, { skipConversationId }
  ) => {    
    state.allConversations.forEach((chat) => {      
      if (chat.dataFetched && chat.status == 'open' && (!skipConversationId || Number(chat.id) !== Number(skipConversationId))) {
        Vue.set(chat, 'syncMessages', true);
      }
    });
  },  

  assignAgent: async ({ dispatch }, { conversationId, agentId, teamId = null }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
        teamId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
    // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    const {
      data: {
        payload: {
          current_status: updatedStatus,
          snoozed_until: updatedSnoozedUntil,
        } = {},
      } = {},
    } = await ConversationApi.toggleStatus({
      conversationId,
      status,
      snoozedUntil,
    });
    commit(types.CHANGE_CONVERSATION_STATUS, {
      conversationId,
      status: updatedStatus,
      snoozedUntil: updatedSnoozedUntil,
    });
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    await dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit, state }, pendingMessage) => {
    try {
      commit(types.SET_MESSAGE_UI_FLAG, {
        isCreating: true,
      });
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = await MessageApi.create(pendingMessage);
      AnalyticsHelper.track(
        pendingMessage.private
          ? CONVERSATION_EVENTS.SENT_PRIVATE_NOTE
          : CONVERSATION_EVENTS.SENT_MESSAGE
      );
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.SET_MESSAGE_UI_FLAG, {
        isCreating: false,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      commit(types.SET_MESSAGE_UI_FLAG, {
        isCreating: false,
      });
      throw error;
    }
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    const {
      conversation: { last_activity_at: lastActivityAt } = {},
      conversation_id: conversationId,
    } = message;
    if (lastActivityAt) {
      commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
        lastActivityAt,
        conversationId,
      });
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;

    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  removeConversation({ commit }, conversation) {
    commit(types.REMOVE_CONVERSATION, conversation);
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, state }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('conversationProductLabels/setConversationProductLabel', {
      id: conversation.id,
      data: conversation.product_labels,
    });

    dispatch('conversationTopicLabels/setConversationTopicLabel', {
      id: conversation.id,
      data: conversation.topic_labels,
    });

    dispatch('conversationConcurrentLabels/setConversationConcurrentLabel', {
      id: conversation.id,
      data: conversation.concurrent_labels,
    });

    dispatch('contacts/setContact', sender);

    const [chat] = getSelectedChatConversation({ ...state, selectedChatId: conversation.id });
    if (chat && chat.dataFetched && !chat.syncMessages && (conversation.last_non_activity_message || (conversation.messages ?? []).length > 0)) {
      let messagesCheckAndAdd = (conversation.messages ?? []);
      if (conversation.last_non_activity_message && !messagesCheckAndAdd.find((message) => message.id === conversation.last_non_activity_message.id)) {
        messagesCheckAndAdd.push(conversation.last_non_activity_message);
      }
      messagesCheckAndAdd = messagesCheckAndAdd.filter((message) => {
        return !chat.messages.some((messageInStore) => messageInStore.id === message.id);
      });
      if (messagesCheckAndAdd.length > 0) {
        messagesCheckAndAdd = messagesCheckAndAdd.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        if (chat.id != state.selectedChatId) {
          Vue.set(chat, 'syncMessages', true);
          dispatch('updateConversationLastActivity', {
            lastActivityAt: messagesCheckAndAdd[messagesCheckAndAdd.length - 1].created_at,
            conversationId: chat.id,
          });
        } else {
          messagesCheckAndAdd.forEach((message) => {
            dispatch('addMessage', message);
          });
        }
      }
    }
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setCampaignFilter({ commit }, filter) {
    commit(types.SET_CAMPAIGN_FILTER, filter);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  downloadTranscriptPdf: async (_, { conversationId }) => {
    try {
      const response = await ConversationApi.downloadTranscriptPdf({
        conversationId,
      });

      const blob = new Blob([response.data], {
        type: 'application/pdf',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `conversa-${conversationId}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setSyncMessagesInConversations({ state, dispatch, commit }, { conversationsIds }) {
    const [activeChat] = getSelectedChatConversation(state);
    state.allConversations.forEach((chat) => {      
      if (conversationsIds.includes(chat.id) && chat.dataFetched && chat.status == 'open') {
        Vue.set(chat, 'syncMessages', true);
        if(activeChat && Number(chat.id) === Number(activeChat.id)){
          dispatch('setActiveChat', {
            data: chat
          });
        }
      }
    });    
  },

  syncConversationsAfterWebsocketReconnect({ dispatch, commit, state }, { disconnectedAt }) {
    ConversationApi.get({
      sort_by: 'latest',
      status: 'all',
      per_page: 1000,
      after_datetime: disconnectedAt
    }).then((response) => {
      const conversationList = response?.data?.data?.payload ?? [];
      conversationList.forEach((conversation) => {
        if (!state.allConversations.some(stateConversation => Number(stateConversation.id) === Number(conversation.id))) {
          dispatch('addConversation', conversation);
        } else {
          const [chat] = getSelectedChatConversation(state);
          if (!chat || Number(chat.id) !== Number(conversation.id)) {
            dispatch('updateConversation', conversation);
          }
        }
      });
    });
  },

  ...messageReadActions,
  ...messageTranslateActions,
  ...messageForwardActions,
  ...messageHideActions,
};

export default actions;
